import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { 
  Autocomplete,
  Box,
  Button, 
  CircularProgress, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SvgIcon,
  TextField,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import constants, { NONE_OF_THE_ABOVE_OPTION } from '../../../constants';
import { 
  ClassYears, 
  GenerateLernsetWorksheet,
  Languages 
} from '@klab-berlin/api-sdk/lib/types/requests/Ai';
import services from '../../../services';
import { SnackbarContext } from '../../../state/SnackbarContextProvider';
import { borderRadius, palette, spaces } from '../../../assets/styles/themes/tokens';
import copyText from '../../../assets/lernsets/icons/copy-text.svg';
import refresh from '../../../assets/lernsets/icons/refresh.svg';
import check from '../../../assets/lernsets/icons/check.svg';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import showdown from 'showdown';
import { WorksheetGenerated } from '../../../ampli';
import { Lernset } from '../../../reducers/lernsets.reducers';

export enum GenerateWorksheetPlacement {
  LernsetsOverviewPage = 'lernsets overview page',
  RichTextEditor = 'rich text editor',
}

interface OwnProps {
  isOpen: boolean;
  onCloseModal: () => void;
  onGenerateWorksheetInsert: (worksheet: string) => void;
  lernset: Lernset;
  placement: GenerateWorksheetPlacement;
}

interface GenerateWorksheetPayload {
  subject: string;
  classYear: typeof ClassYears[number];
  language: typeof Languages[number];
  topic: string;
  instructions: string;
}

const GenerateWorksheetDialog: React.FC<OwnProps> = (props) => {
  const { t } = useTranslation();
  const { openSnackbar } = useContext(SnackbarContext);
  const { 
    isOpen,
    onCloseModal,
    onGenerateWorksheetInsert,
    lernset,
    placement
  } = props;

  const markdownRef = useRef<HTMLInputElement>(null);
  const [isGeneratingWorksheet, setIsGeneratingWorksheet] = useState(false);
  const [inputSubject, setInputSubject] = useState('');
  const [worksheet, setWorksheet] = useState('');
  const [isCopied, setIsCopied] = useState(false);

  const GenerateWorksheetFormValidator = yup.object<GenerateLernsetWorksheet>({
    subject: yup.string().required(t('This field is required')),
    classYear: yup.mixed().oneOf(Object.values(ClassYears)).required(),
    language: yup.mixed().oneOf(Object.values(Languages)).required(),
    topic: yup
      .string()
      .required(t('This field is required'))
      .max(300, t('The maximum length of the topic must not be exceeded')),
    instructions: yup.string().max(1000).notRequired(),
  });

  const {
    errors,
    handleSubmit,
    register,
    formState,
    control,
    reset
  } = useForm<GenerateWorksheetPayload>({
    mode: 'onBlur', 
    validationSchema: GenerateWorksheetFormValidator, 
    defaultValues: {
      subject: '',
      classYear: ClassYears[8],
      language: Languages[0],
      topic: '',
      instructions: '',
    } });

  const germanSubjects = useMemo(
    () => constants.subjects
      .filter((subject) => subject.id !== NONE_OF_THE_ABOVE_OPTION && subject.englishTranslation)
      .map((subject) => subject.text),
    [constants.subjects],
  );

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (isCopied) {
      timeout = setTimeout(() => setIsCopied(false), 5000);
    }

    return () => clearTimeout(timeout);
  }, [isCopied]);

  const handleCopy = async () => {
    const errorNotification = () => {
      openSnackbar({
        type: 'error',
        message: t('The worksheet was not copied. Please try again'),
      });
    };

    if (markdownRef.current) {
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([markdownRef.current.innerHTML], { type: 'text/html' }),
            })
          ]);
          setIsCopied(true);
        } else {
          const range = document.createRange();
          range.selectNodeContents(markdownRef.current);
          const selection = window.getSelection();
          if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
            document.execCommand('copy');
            setIsCopied(true);
          }
        }
      } catch (error) {
        errorNotification();
      }
    }
  };

  const trackWorksheetGenerated = (worksheet: GenerateWorksheetPayload, lernset: Lernset) => {
    const { subject, classYear, language, topic, instructions } = worksheet;
    const eventProperties = {
      ...(instructions ? { 'generator additional info': instructions } : {}),
      'generator grade': parseInt(classYear, 10),
      'generator language': language,
      'generator subject': subject,
      'generator topic': topic,
      'lernset id': lernset.id,
      'lernset title': lernset.title,
      'page source': placement === GenerateWorksheetPlacement.LernsetsOverviewPage
        ? 'add content cta' 
        : 'rich text menu'
    };
    services.track.ampliEventTrack({ 
      event: new WorksheetGenerated(eventProperties)
    });   
  };

  const handleConfirm = async (payload: GenerateWorksheetPayload) => {
    try {
      setWorksheet('');
      setIsGeneratingWorksheet(true);
      const { subject, classYear, language, topic, instructions } = payload;
      const englishSubject = constants.subjects.find((s) => s.text === subject)?.englishTranslation;
      if (englishSubject) {
        const worksheetText = 
          await services.ai.generateLernsetWorksheet({
            subject: englishSubject,
            classYear,
            language,
            topic,
            ...(instructions ? { instructions } : {}),
          });
        setWorksheet(worksheetText);
        trackWorksheetGenerated(payload, lernset);
      }
    } catch (error: any) {
      const errorMessage = (error?.message ?? '').toLowerCase();
      const snackbarMessage = errorMessage.includes('rate limit exceeded') 
        ? 'You exceeded your daily rate limit. You can generate new worksheets tomorrow.' 
        : 'Worksheet could not be generated. Please try again.';
       
      openSnackbar({
        type: 'error',
        message: t(snackbarMessage),
      });
    }
  
    setIsGeneratingWorksheet(false);
  };

  const onSubmit = handleSubmit(handleConfirm);

  const insertWorksheetInHTMLFormat = () => {
    if (worksheet) {
      const converter = new showdown.Converter({
        simpleLineBreaks: true,
      });
      const worksheetHtml = converter.makeHtml(worksheet.replace(/_/g, '&#95;'));
      onGenerateWorksheetInsert(worksheetHtml);
      setWorksheet('');
      reset();
      onCloseModal();  
    }
  };

  return (
    <Dialog open={isOpen}
      onClose={onCloseModal}
    >
      <DialogTitle sx={{ typography: 'h5', padding: spaces.lg }}>
        {t('Generate worksheets')}
      </DialogTitle>
      <form onSubmit={onSubmit}>
        <fieldset disabled={formState.isSubmitting}>
          <DialogContent
            sx={{
              overflow: 'visible', 
              padding: `${spaces.md} ${spaces.lg} ${spaces.lg} ${spaces.lg}` 
            }}
          >
            <Typography>
              {t('Enter the relevant information to create a customised worksheet for your class.')}
            </Typography>
            <Box sx={{ mt: spaces.lg }}>
              <FormControl fullWidth>
                <Controller
                  name='subject'
                  control={control}
                  onChange={([, data]) => data}
                  as={
                    <Autocomplete
                      id='subject-select'
                      inputValue={inputSubject}
                      onInputChange={(_, newInputValue) => setInputSubject(newInputValue)}
                      disableClearable
                      options={germanSubjects}
                      isOptionEqualToValue={(option, value) => value === '' || option === value ? true : false}
                      getOptionLabel={(option) => option}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name='subject'
                          data-testid='subject-textfield'
                          label={t('Subject')}
                          variant='standard'
                          required
                          disabled={isGeneratingWorksheet}
                          error={!!errors?.subject}
                          helperText={errors?.subject?.message}
                          placeholder={t('Select the subject')}
                          InputLabelProps={{ shrink: true }}
                          inputRef={register}
                          id='subject'
                        />
                      )}
                    />
                  }
                />
              </FormControl>
            </Box>
            <Box
              sx={{
                mt: spaces.lg,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: spaces.xs
              }}>
              <FormControl sx={{ minWidth: 180 }} fullWidth variant='standard'>
                <InputLabel id='class-year-select-standard-label'>{t('Class years')}</InputLabel>
                <Controller
                  name='classYear'
                  control={control}
                  as={<Select
                    name='classYear'
                    labelId='class-year-select-standard-label'
                    id='class-year-select-standard'
                    label={t('Class years')}
                    defaultValue={ClassYears[0]}
                    disabled={isGeneratingWorksheet}
                  >
                    {ClassYears.map((classYear) => (
                      <MenuItem key={classYear} value={classYear}>{`${parseInt(classYear, 10)}.`}</MenuItem>
                    ))}
                  </Select>}
                />
              </FormControl>
              <FormControl sx={{ minWidth: 120 }} fullWidth variant='standard'>
                <InputLabel id='language-select-standard-label'>{t('Language')}</InputLabel>
                <Controller
                  name='language'
                  control={control}
                  as={<Select
                    name='language'
                    labelId='language-select-standard-label'
                    id='language-select-standard'
                    label={t('Language')}
                    defaultValue={t(Languages[0])}
                    disabled={isGeneratingWorksheet}
                  >
                    {Languages.map((language) => (
                      <MenuItem key={language} value={language}>{t(language)}</MenuItem>
                    ))}
                  </Select>}
                />
              </FormControl>
            </Box>
            <Box sx={{ mt: spaces.lg }}>
              <FormControl fullWidth>
                <TextField
                  data-testid='topic-textfield'
                  name='topic'
                  label={t('SubTopic')}
                  variant='standard'
                  type='text'
                  placeholder={t('Choose a topic (max. 300 characters)')}
                  required
                  disabled={isGeneratingWorksheet}
                  error={!!errors?.topic}
                  helperText={errors?.topic?.message}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ 
                    maxLength: 150,
                    style: {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }, 
                  }}
                  inputRef={register}
                  id='topic'
                />
              </FormControl>
            </Box>
          </DialogContent>
          <DialogContent sx={{ pt: 0, pb: spaces.lg }}>
            <FormControl fullWidth>
              <TextField
                data-testid='instructions-textfield'
                name='instructions'
                label={t('Additional instructions')}
                variant='standard'
                type='text'
                placeholder={t('Add relevant information, e.g. special requirements or learning objectives')}
                disabled={isGeneratingWorksheet}
                error={!!errors?.instructions}
                helperText={errors?.instructions?.message}
                multiline
                minRows={2}
                InputLabelProps={{ shrink: true }}
                inputRef={register}
                id='instructions'
              />
            </FormControl>
            <Typography sx={{ mt: spaces.xl, fontSize: 12, lineHeight: '16px' }}>
              {t('The results generated by the AI may be incorrect or misleading. Always check the tasks yourself.')}
            </Typography>
          </DialogContent>
          <DialogContent sx={{ maxHeight: 300, py: 0, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
              {isGeneratingWorksheet && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignSelf: 'center',
                  width: '100%',
                  mb: spaces.sm,
                  pt: spaces.lg,
                }}>
        
                  <CircularProgress sx={{
                    '& .MuiCircularProgress-svg': {
                      height: 'auto',
                      width: 'auto',
                    },
                  }} />
                </Box>
              )}
              {worksheet && (
                <Box 
                  ref={markdownRef}
                  sx={{
                    borderRadius: borderRadius.sm,
                    border: `1px solid ${palette.primary.outlinedBorder}`,
                    padding: spaces.lg,
                    backgroundColor: palette.primary.hover,
                    height: '100%',
                    textAlign: 'justify',
                    px: spaces.lg,
                    pb: 0,
                    width: '100%',
                    pt: spaces.lg,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}>
                  <Box
                    sx={{
                      borderRadius: borderRadius.sm,
                      border: `1px solid ${palette.elevation.enabled}`,
                      padding: spaces.lg,
                      backgroundColor: palette.background.paperElevation2,
                      textAlign: 'justify',
                      p: spaces.sm,
                      overflow: 'auto',
                      flexGrow: 1,
                    }}
                  >
                    <ReactMarkdown>
                      {worksheet.replace(/<br>/g, '  \n')}
                    </ReactMarkdown>
                  </Box>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: spaces.xs,
                    alignItems: 'center',
                    mt: spaces.xxs,
                  }}>
                    <Tooltip title={t('Copy content')}>
                      <IconButton onClick={handleCopy}>
                        <SvgIcon
                          component={isCopied ? check : copyText} 
                          sx={{ fill: palette.primary.main }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('Regenerate')}>
                      <IconButton onClick={onSubmit}>
                        <SvgIcon
                          component={refresh} 
                          sx={{ fill: palette.primary.main }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: spaces.lg }}>
            <Button
              disabled={isGeneratingWorksheet}
              onClick={() => {
                setWorksheet('');
                onCloseModal();
              }}
              variant='text'
              size='large'
            >
              {t('Cancel')}
            </Button>
            {worksheet ? (
              <Button
                variant='contained'
                size='large'
                onClick={insertWorksheetInHTMLFormat}
                data-testid='insert-worksheet-button'
              >
                {t('Insert')}
              </Button>
            ) : (
              <Button
                disabled={isGeneratingWorksheet}
                variant='contained'
                size='large'
                type='submit'
                data-testid='generate-worksheet-button'
              >
                {t('Generate')}
              </Button>
            )}
          </DialogActions>
        </fieldset>
      </form>
    </Dialog>
  );
};

export default GenerateWorksheetDialog;

